import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { motion } from "framer-motion";

import { useNavigate, useSearchParams } from "react-router-dom";

import {
  useHasPermission,
  useIsSystem,
} from "../../contexts/PermissionsProvider";
import AccessMembers from "../Access/AccessMembers";
import CopyLinkModal from "../Access/CopyLinkModal";
import SystemInviteUserDialog from "./SystemInviteUserDialog";
import AssignmentPickerDialog from "../../components/Directory/AssignmentPickerDialog";
import { resetUserNda } from "../../helpers/actions";
import {
  ActionRow,
  EmptyState,
  InlineFilter,
  PageHeader,
  ScopeSelector,
  SkeletonTable,
  StatusPill,
} from "../../components/Directory/primitives";
import { dirTokens } from "../../components/Directory/tokens";
import { getAccounts, getUsers } from "./api";

/**
 * DirectoryUsers — role-adaptive.
 *
 * SYSTEM callers get the cross-tenant users table (ported from
 * clue2app-admin/frontend/src/components/UsersTab.tsx). ACCOUNT_ADMIN
 * callers get the existing <AccessMembers/> surface mounted verbatim
 * so we don't fork the members-write flow — Invite / Change role /
 * Remove keep working, and the nav label swaps to "Members" via
 * DIRECTORY_TAB_LABELS.
 */

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function accountsCount(u) {
  if (Array.isArray(u.accounts)) return u.accounts.length;
  // Legacy single-account shape from coord: {accountId, accountName}.
  if (u.accountId || u.accountName) return 1;
  return 0;
}

const COLUMN_SPEC = [
  { label: "Email" },
  { label: "Name" },
  { label: "Type", width: 100 },
  { label: "Accounts", align: "right", width: 96 },
  { label: "Status", width: 120 },
  { label: "Created", width: 140 },
  { label: "ID", mono: true },
  { label: "", align: "right", width: 80 },
];

function SystemUsersTable() {
  const navigate = useNavigate();
  const [users, setUsers] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");

  // Row kebab + Add-to-group picker.
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [addGroupRow, setAddGroupRow] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [toast, setToast] = useState(null);

  // SCK-741 — SYSTEM-user invite surface. Previously missing: the
  // cross-tenant SystemUsersTable had no invite CTA, forcing SYSTEM
  // operators to drill Directory → Accounts → <row> → Invite user,
  // which bypasses CopyLinkModal (no branded Email tab). Now the
  // header has an "Invite user" button that opens SystemInviteUserDialog
  // and the mint response flows through the same handleInviteResponse
  // helper AccessMembers uses, so SCK-738's Email tab fires here too.
  const [inviteOpen, setInviteOpen] = useState(false);
  const [copyLink, setCopyLink] = useState(null);
  // null | { url, email, expiresOn }

  const handleInviteResponse = ({ email, response }) => {
    const invitation = response?.invitation;
    if (invitation && invitation.deliveryMode === "email") {
      setToast({ message: `Invitation sent to ${email}.` });
      return;
    }
    if (invitation && invitation.redemptionUrl) {
      setCopyLink({
        url: invitation.redemptionUrl,
        email,
        expiresOn: invitation.expires_on || invitation.expiresOn || null,
      });
      setToast({ message: "Invitation minted — ready to copy." });
      return;
    }
    setToast({ message: `Invited ${email}.` });
  };

  // SCK-714. Prefer entitlement verbs over role-name strings — SYSTEM
  // callers are covered by the wildcard short-circuit inside
  // useHasPermission (see PermissionsProvider). `directory.user.manage`
  // covers SYSTEM_DIRECTORY_ADMIN + ACCOUNT_DIRECTORY_ADMIN;
  // `member.manage` covers ACCOUNT_ADMIN.
  const canResetNda = useHasPermission([
    "directory.user.manage",
    "member.manage",
  ]);

  const openMenu = (e, row) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuRow(row);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  // Cross-tenant Users list surfaces users that may belong to multiple
  // accounts; the /api/users payload can carry `accounts[]` or the
  // legacy single-account shape. We pick the first account as the group
  // scope — the picker itself lists only groups visible to the caller
  // under that scope. For a truly platform-scoped user with no account
  // (unusual), we fall back to null and rely on coord to accept the
  // POST /groups/{gid}/members/{uid} route under SYSTEM.
  const rowScope = (u) => {
    if (!u) return null;
    if (Array.isArray(u.accounts) && u.accounts.length) return u.accounts[0].id;
    return u.accountId || null;
  };

  // URL-persisted scope: ?scope=all|account & ?accountId=<uuid>
  const [searchParams, setSearchParams] = useSearchParams();
  const scope = searchParams.get("scope") === "account" ? "account" : "all";
  const accountId = searchParams.get("accountId") || null;

  // Accounts for the picker — lazy-loaded on first render of the
  // SYSTEM table since ACCOUNT_ADMIN callers never mount this component.
  const [accounts, setAccounts] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getAccounts()
      .then((data) => {
        if (!cancelled) setAccounts(data || []);
      })
      .catch(() => {
        // Non-fatal — the selector still works; the picker just shows
        // "No accounts" and the user can clear the pinned id.
        if (!cancelled) setAccounts([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Users load — re-fires whenever the accountId narrow changes.
  useEffect(() => {
    let cancelled = false;
    setUsers(null);
    setErr(null);
    getUsers({ accountId: scope === "account" ? accountId : null })
      .then((data) => {
        if (!cancelled) setUsers(data);
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(
          e.status === 403
            ? "SYSTEM privileges required to view all users."
            : `Failed to load users: ${e.message}`
        );
      });
    return () => {
      cancelled = true;
    };
  }, [scope, accountId]);

  const handleScopeChange = ({ scope: nextScope, accountId: nextAccountId }) => {
    const next = new URLSearchParams(searchParams);
    if (nextScope && nextScope !== "all") next.set("scope", nextScope);
    else next.delete("scope");
    if (nextAccountId) next.set("accountId", nextAccountId);
    else next.delete("accountId");
    setSearchParams(next, { replace: true });
  };

  const filtered =
    users?.filter((u) => {
      if (statusFilter === "active" && u.enabled === false) return false;
      if (!q) return true;
      const needle = q.toLowerCase();
      return (
        (u.name ?? "").toLowerCase().includes(needle) ||
        (u.email ?? "").toLowerCase().includes(needle) ||
        (u.id ?? "").toLowerCase().includes(needle)
      );
    }) ?? [];

  const disabledCount =
    users?.filter((u) => u.enabled === false).length ?? 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
    >
      <PageHeader
        title="Users"
        titleInfo="Every real person who signs in. A user can belong to many accounts, each with their own role."
        subtitle="Cross-tenant list of users on the platform. Click a row to view accounts, group memberships, and assigned roles."
        action={
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setInviteOpen(true)}
            data-testid="system-invite-user-cta"
          >
            Invite user
          </Button>
        }
      />

      {err && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      )}

      <ScopeSelector
        variant="users"
        value={{ scope, accountId }}
        onChange={handleScopeChange}
        accounts={accounts}
        sx={{ mb: 2 }}
      />

      {!users && !err && <SkeletonTable columns={COLUMN_SPEC} rows={6} />}

      {users && (
        <>
          <InlineFilter
            value={q}
            onChange={setQ}
            placeholder="Filter by name, email, or id"
            count={{
              shown: filtered.length,
              total: users.length,
              noun: "user",
            }}
          >
            <ToggleButtonGroup
              size="small"
              value={statusFilter}
              exclusive
              onChange={(_, v) => v && setStatusFilter(v)}
              aria-label="user status filter"
            >
              <ToggleButton value="active" aria-label="active only">
                Active
              </ToggleButton>
              <ToggleButton
                value="all"
                aria-label="show all including disabled"
              >
                All {disabledCount > 0 && `(+${disabledCount} disabled)`}
              </ToggleButton>
            </ToggleButtonGroup>
          </InlineFilter>

          {filtered.length === 0 ? (
            <EmptyState
              icon={<PeopleOutlinedIcon />}
              title={q ? "No users match" : "No users yet"}
              description={
                q
                  ? "Try clearing the filter or switching to All to include disabled users."
                  : "Users appear here as they redeem invitations across accounts."
              }
              variant={q ? "no-match" : "empty"}
            />
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: dirTokens.radius }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {COLUMN_SPEC.map((c, i) => (
                      <TableCell
                        key={c.label || `col-${i}`}
                        align={c.align || "left"}
                        sx={c.mono ? { fontFamily: "monospace" } : undefined}
                      >
                        {c.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((u) => (
                    <ActionRow
                      key={u.id}
                      sx={{ cursor: "pointer" }}
                      onClick={() => navigate(`/directory/users/${u.id}`)}
                      aria-label={`View ${u.email || u.name || "user"} — identity, accounts, groups, and roles`}
                    >
                      <TableCell>{u.email || "—"}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {u.name || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={
                            u.userType === "SYSTEM"
                              ? "SYSTEM users are platform operators — cross-tenant, superuser."
                              : "Regular platform user, scoped to the accounts they belong to."
                          }
                          arrow
                          enterDelay={400}
                        >
                          <Chip
                            label={u.userType || "USER"}
                            size="small"
                            color={
                              u.userType === "SYSTEM" ? "secondary" : "default"
                            }
                            variant="outlined"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <span
                          style={{
                            fontVariantNumeric: "tabular-nums",
                            fontSize: 13,
                          }}
                        >
                          {accountsCount(u)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <StatusPill
                          status={u.enabled === false ? "disabled" : "active"}
                        />
                      </TableCell>
                      <TableCell>{formatDate(u.createdOn)}</TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.75rem",
                          color: "text.secondary",
                        }}
                      >
                        {u.id}
                      </TableCell>
                      <TableCell align="right" sx={{ width: 80 }}>
                        <Box
                          className="row-actions"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            color: "text.secondary",
                          }}
                        >
                          <Tooltip title="More actions">
                            <IconButton
                              size="small"
                              onClick={(e) => openMenu(e, u)}
                              aria-label="User actions"
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Open user detail">
                            <ChevronRightIcon fontSize="small" />
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </ActionRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
      >
        <Tooltip
          title="Grant this user access via group membership. Roles held by the group apply automatically."
          placement="left"
        >
          <MenuItem
            onClick={() => {
              setAddGroupRow(menuRow);
              closeMenu();
            }}
          >
            Add to group…
          </MenuItem>
        </Tooltip>
        <MenuItem
          onClick={() => {
            const row = menuRow;
            closeMenu();
            if (row) navigate(`/directory/users/${row.id}`);
          }}
        >
          Open user detail
        </MenuItem>
        {canResetNda && (
          <Tooltip
            title="Force this user to re-accept the NDA on their next login. Account membership, roles, and project access are unchanged."
            placement="left"
          >
            <MenuItem
              onClick={() => {
                setResetTarget(menuRow);
                closeMenu();
              }}
              data-testid={
                menuRow ? `reset-nda-action-${menuRow.id}` : "reset-nda-action"
              }
            >
              Reset NDA
            </MenuItem>
          </Tooltip>
        )}
      </Menu>

      {addGroupRow && (
        <AssignmentPickerDialog
          open
          mode="add_to_group"
          userId={addGroupRow.id}
          scopeAccountId={rowScope(addGroupRow)}
          onClose={() => setAddGroupRow(null)}
          onSaved={({ count }) => {
            setAddGroupRow(null);
            setToast({
              message: `Added to ${count} group${count === 1 ? "" : "s"}.`,
            });
          }}
        />
      )}

      {resetTarget && (
        <ResetNdaConfirmDialog
          user={resetTarget}
          onClose={() => setResetTarget(null)}
          onDone={({ severity, message }) => {
            setResetTarget(null);
            setToast({ severity, message });
          }}
        />
      )}

      <SystemInviteUserDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSaved={({ email, response }) => {
          setInviteOpen(false);
          handleInviteResponse({ email, response });
        }}
      />

      <CopyLinkModal
        open={!!copyLink}
        url={copyLink?.url || ""}
        email={copyLink?.email || ""}
        expiresOn={copyLink?.expiresOn || null}
        onClose={() => setCopyLink(null)}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert
            severity={toast.severity || "success"}
            onClose={() => setToast(null)}
            sx={{ width: "100%" }}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </motion.div>
  );
}

/**
 * ResetNdaConfirmDialog — SCK-714.
 *
 * Mirrors the inline-per-action confirmation pattern used by
 * AccessMembers.RemoveConfirmDialog: local `submitting` + inline error
 * `Typography color:error.main`, MUI `<Dialog maxWidth="xs" fullWidth>`,
 * destructive Cancel/Confirm actions. Parent owns the toast — this
 * dialog surfaces success/failure via `onDone({ severity, message })`.
 */
function ResetNdaConfirmDialog({ user, onClose, onDone }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const label = user?.email || user?.name || `user ${user?.id}`;

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const { message } = await resetUserNda(user.id, user.email);
      onDone({ severity: "success", message });
    } catch (err) {
      setError(err?.message || "Reset failed. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      onClose={submitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Reset NDA for {label}?</DialogTitle>
      <DialogContent dividers>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, mb: 1 }}
          data-testid="reset-nda-target-email"
        >
          {label}
        </Typography>
        <Typography variant="body2">
          This user will be prompted to re-accept the NDA on their next login.
          Their account membership, roles, and project access are unchanged.
          This action is logged.
        </Typography>
        {error && (
          <Typography sx={{ color: "error.main", mt: 2 }} variant="body2">
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={handleConfirm}
          disabled={submitting}
          data-testid="reset-nda-confirm"
        >
          {submitting ? "Resetting…" : "Reset NDA"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function DirectoryUsers({ currentUser }) {
  // Cross-tenant view is chosen by `useIsSystem()` — JWT `userType ===
  // "SYSTEM"` first, then the SYSTEM wildcard or `directory.account.manage`
  // entitlement in the reactive snapshot. Sourcing this from the JWT
  // user (not just `permissions.includes("SYSTEM")`) is deliberate:
  // the SCK-547 `/api/me/permissions` payload doesn't always echo the
  // synthetic "SYSTEM" verb, so a SYSTEM caller was falling through to
  // AccessMembers and seeing only their own account's members. Post
  // PR-1.5f we no longer gate on role-name strings; the old
  // SYSTEM_DIRECTORY_ADMIN name is renamed to DIRECTORY_ADMIN in V88.
  const isSystem = useIsSystem();

  // ACCOUNT_ADMIN case: mount the existing own-account members surface
  // verbatim. All writes (Invite / Change role / Remove) come along
  // for free; the parent Directory hero + rail wrap it.
  if (!isSystem) {
    return <AccessMembers currentUser={currentUser} />;
  }
  return <SystemUsersTable />;
}
